import { PAEDIATRIC_AGE_BAND } from './enums.js';
import { PAEDIATRIC_HILLO } from './hillos.js';
import { procedureByKey } from './procedures.js';
/**
 * Values computed from an entry, never stored.
 *
 * Everything here works in the **browser's local timezone**, deliberately: that
 * is where the trainee worked, and a night shift in Bristol is a night shift
 * regardless of what UTC thinks. Using `Date`'s local accessors gives that for
 * free, including across British Summer Time.
 *
 * These feed the temporal distribution section of the PDF, which is a panel's
 * only view of out-of-hours workload. An off-by-one here misrepresents a
 * trainee's working pattern to the people judging their progression, so the
 * boundaries are tested explicitly.
 */
/** Day runs 08:00–19:59 inclusive; night is 20:00–07:59. */
export const DAY_STARTS_HOUR = 8;
export const NIGHT_STARTS_HOUR = 20;
/**
 * Drives HiLLO 13 evidence in the PDF. Never a user-facing toggle — it follows
 * from the age band, which is the only thing anyone is asked for.
 */
export function isPaediatric(entry) {
    return entry.ageBand === PAEDIATRIC_AGE_BAND;
}
export function timeOfDay(occurredAt) {
    const hour = occurredAt.getHours();
    return hour >= DAY_STARTS_HOUR && hour < NIGHT_STARTS_HOUR ? 'day' : 'night';
}
/**
 * Saturday or Sunday in local time.
 *
 * Note that a night shift starting Friday 21:00 counts as a weekday night, not
 * a weekend one. Classification follows the recorded start time rather than
 * trying to infer where a shift ended, because the app records a moment and not
 * a duration, and guessing would be worse than being simple and predictable.
 */
export function isWeekend(occurredAt) {
    const day = occurredAt.getDay();
    return day === 0 || day === 6;
}
export function dayPart(occurredAt) {
    const weekend = isWeekend(occurredAt);
    const night = timeOfDay(occurredAt) === 'night';
    if (weekend)
        return night ? 'weekend_night' : 'weekend_day';
    return night ? 'weekday_night' : 'weekday_day';
}
/* ------------------------------------------------------- HiLLO suggestions */
/** By case type, per CURRICULUM.md. */
const CASE_TYPE_HILLOS = {
    admission: [5, 6],
    ward_review: [5, 11],
    remote_advice: [5, 11],
    /**
     * Same pair as an admission, and for the same reason: 6 is investigation,
     * monitoring and organ support, which is what continuing care on the unit
     * is, and 5 covers the resuscitation and stabilisation role this case type
     * exists partly to capture.
     */
    notable_icu_case: [5, 6],
};
/**
 * By EDT activity, per CURRICULUM.md.
 *
 * Sparse on purpose. Teaching you deliver is HiLLO 4 by definition; teaching
 * you attend is not, and neither is reading or a clinic — those may evidence
 * any HiLLO at all depending on their subject, and a suggestion that is wrong
 * more often than it is right teaches people to ignore the row. Where there is
 * nothing confident to say, this says nothing.
 */
const EDT_ACTIVITY_HILLOS = {
    teaching_delivered: [4],
    teaching_attended: [],
    clinic: [],
    self_directed_learning: [],
    project_audit: [2],
    research: [3],
    course_conference: [],
    simulation: [5],
    management: [1, 9],
    exam_preparation: [],
    other: [],
};
/**
 * The HiLLOs proposed when the Curriculum disclosure is opened.
 *
 * **Suggestions only.** They render as unfilled chips and nothing is stored
 * until the user taps one. A trainee who never opens the disclosure is a
 * supported user, and the PDF simply omits the curriculum section.
 */
export function suggestHillos(input) {
    const suggested = new Set();
    /*
     * EDT returns early: it has no age band, so the paediatric rule below cannot
     * apply to it, and none of the case rules do either.
     */
    if (input.kind === 'edt') {
        return [...EDT_ACTIVITY_HILLOS[input.activity]];
    }
    if (input.kind === 'procedure') {
        for (const hillo of procedureByKey(input.procedureKey)?.suggestedHillos ?? []) {
            suggested.add(hillo);
        }
    }
    else {
        for (const hillo of CASE_TYPE_HILLOS[input.caseType]) {
            suggested.add(hillo);
        }
        const reasons = input.reasonsForReferral ?? [];
        // End-of-life care and organ donation are HiLLO 8 by definition.
        if (reasons.includes('end_of_life') || reasons.includes('organ_donation')) {
            suggested.add(8);
        }
        // Either flavour of post-operative admission is perioperative care.
        if (reasons.includes('post_op_elective') || reasons.includes('post_op_emergency')) {
            suggested.add(7);
        }
        // Teaching or supervising anyone during the case is HiLLO 4 by definition.
        if ((input.teachingGrades ?? []).length > 0) {
            suggested.add(4);
        }
    }
    // Any entry in a patient under 16 is paediatric ICM, whatever else it is.
    if (input.ageBand === PAEDIATRIC_AGE_BAND) {
        suggested.add(PAEDIATRIC_HILLO);
    }
    return [...suggested].sort((a, b) => a - b);
}
//# sourceMappingURL=derive.js.map